export const PROBE_KEY = 'eliadmin.navigation-diagnostics.until';
export const PROBE_TTL_MS = 10 * 60 * 1000;
export const PROBE_LIMIT = 80;
export const PROBE_HANDOFF_KEY = 'eliadmin.navigation-diagnostics.handoff';
export const PROBE_HANDOFF_TTL_MS = 30_000;
export const PROBE_HANDOFF_LIMIT = 6;
export const PROBE_HANDOFF_BYTES = 4096;
export type ProbeEntry = Record<string, string | number | boolean>;

export function navigationType(value: unknown) {
  return typeof value === 'string' &&
    ['push', 'replace', 'reload', 'traverse', 'navigate', 'back_forward'].includes(value)
    ? value
    : 'unknown';
}

/** Keep code identifiers and hashed same-origin files, never stack messages or URL contents. */
export function summarizeCallerStack(stack: unknown, origin: string) {
  const frames: string[] = [];
  let externalFrames = 0;
  if (typeof stack !== 'string') return { caller: '', externalFrames };
  for (const line of stack.slice(0, 8192).split('\n').slice(1, 13)) {
    const match = line.match(/(https?:\/\/[^\s)]+):(\d+):(\d+)\)?\s*$/);
    if (!match) continue;
    try {
      const url = new URL(match[1]);
      if (url.username || url.password) continue;
      if (url.origin !== origin) {
        externalFrames++;
        continue;
      }
      const name =
        line.match(/^\s*at\s+(?:async\s+)?([A-Za-z_$][\w.$]{0,47})\s*\(/)?.[1] ?? 'anonymous';
      frames.push(
        `${name}@${pathKey(url.pathname)}:${match[2].slice(0, 7)}:${match[3].slice(0, 7)}`,
      );
      if (frames.length === 4) break;
    } catch {
      // Malformed and non-HTTP stack frames are not diagnostic evidence.
    }
  }
  return { caller: frames.join('|'), externalFrames };
}

type ProbeHandoff = {
  version: 1;
  savedAt: number;
  expiresAt: number;
  document: ProbeEntry;
  events: ProbeEntry[];
};
const HANDOFF_KINDS = new Set([
  'client-mounted',
  'link-settled',
  'navigation-start',
  'document-unload',
]);
const BOOLEAN_FIELDS = new Set([
  'defaultPrevented',
  'isTrusted',
  'userInitiated',
  'hashChange',
  'canIntercept',
  'sameDocument',
  'sourcePresent',
  'redirectMeta',
  'projectChunkRecovery',
  'coreChunkRecovery',
]);
const NUMBER_FIELDS = new Set([
  'atMs',
  'sinceClickMs',
  'timeOrigin',
  'documentTtfbMs',
  'documentEndMs',
  'externalFrames',
]);
function isSafeEntry(input: unknown): input is ProbeEntry {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return false;
  const record = input as ProbeEntry;
  if (!HANDOFF_KINDS.has(String(record.kind))) return false;
  return Object.entries(record).every(([key, value]) => {
    if (key === 'kind') return true;
    if (['path', 'from', 'to', 'source'].includes(key))
      return typeof value === 'string' && /^(?:[a-f0-9]{8}|external|none)$/.test(value);
    if (key === 'navigationType') return value === navigationType(value);
    if (key === 'caller')
      return (
        typeof value === 'string' &&
        value.length <= 400 &&
        /^(?:[A-Za-z_$][\w.$]{0,47}@[a-f0-9]{8}:\d{1,7}:\d{1,7})(?:\|[A-Za-z_$][\w.$]{0,47}@[a-f0-9]{8}:\d{1,7}:\d{1,7}){0,3}$|^$/.test(
          value,
        )
      );
    if (BOOLEAN_FIELDS.has(key)) return typeof value === 'boolean';
    if (NUMBER_FIELDS.has(key))
      return (
        typeof value === 'number' &&
        Number.isSafeInteger(value) &&
        value >= -1 &&
        value <= Number.MAX_SAFE_INTEGER
      );
    return false;
  });
}

export function readProbeHandoff(
  raw: string | null,
  now: number,
  activeUntil: number,
): ProbeHandoff | null {
  if (!raw || raw.length > PROBE_HANDOFF_BYTES || !resolveProbeExpiry('', String(activeUntil), now))
    return null;
  try {
    const data = JSON.parse(raw) as ProbeHandoff;
    if (
      !data ||
      Object.keys(data).sort().join(',') !== 'document,events,expiresAt,savedAt,version' ||
      data.version !== 1 ||
      !Number.isSafeInteger(data.savedAt) ||
      data.savedAt > now ||
      data.savedAt < now - PROBE_HANDOFF_TTL_MS ||
      !Number.isSafeInteger(data.expiresAt) ||
      data.expiresAt <= now ||
      data.expiresAt > data.savedAt + PROBE_HANDOFF_TTL_MS ||
      data.expiresAt > activeUntil ||
      !isSafeEntry(data.document) ||
      data.document.kind !== 'client-mounted' ||
      !Array.isArray(data.events) ||
      data.events.length > PROBE_HANDOFF_LIMIT ||
      !data.events.every(event => isSafeEntry(event) && event.kind !== 'client-mounted')
    )
      return null;
    return data;
  } catch {
    return null;
  }
}

export function createProbeHandoff(
  document: ProbeEntry,
  events: ProbeEntry[],
  now: number,
  activeUntil: number,
) {
  const data = {
    version: 1,
    savedAt: now,
    expiresAt: Math.min(now + PROBE_HANDOFF_TTL_MS, activeUntil),
    document,
    events: events.slice(-PROBE_HANDOFF_LIMIT),
  };
  let serialized = JSON.stringify(data);
  while (serialized.length > PROBE_HANDOFF_BYTES && data.events.length) {
    data.events.shift();
    serialized = JSON.stringify(data);
  }
  return readProbeHandoff(serialized, now, activeUntil) ? serialized : null;
}

export function resolveProbeExpiry(hash: string, saved: string | null, now: number) {
  if (hash === '#eliadmin-diag-off') return 0;
  if (hash === '#eliadmin-diag') return now + PROBE_TTL_MS;
  const expiry = Number(saved);
  return Number.isFinite(expiry) && expiry > now && expiry <= now + PROBE_TTL_MS ? expiry : 0;
}

// Correlation only, not a security hash. Never retain URL query/hash values or raw paths.
export function pathKey(pathname: string) {
  let hash = 2166136261;
  for (const char of pathname) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function summarizeResource(name: string, origin: string) {
  try {
    const url = new URL(name, origin);
    if (url.origin !== origin) return null;
    const kind = url.pathname.startsWith('/_next/static/')
      ? 'chunk'
      : url.searchParams.has('_rsc')
        ? 'rsc'
        : url.pathname.startsWith('/api/')
          ? 'api'
          : null;
    return kind ? { kind, path: pathKey(url.pathname) } : null;
  } catch {
    return null;
  }
}
